import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories, articles } from '@/mocks/articles';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const categoryArticles = articles.filter((a) => a.category === activeSlug);

  const handleCategorySwitch = (newSlug: string) => {
    setActiveSlug(newSlug);
  };

  return (
    <>
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${activeCategory.name} - 彩金攻略`,
          url: typeof window !== 'undefined' ? window.location.href : '',
          description: activeCategory.description,
          inLanguage: 'zh-CN',
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: typeof window !== 'undefined' ? window.location.origin + '/' : '' },
            { '@type': 'ListItem', position: 2, name: activeCategory.name },
          ],
        }}
      />
      <div className="py-8 md:py-12 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: activeCategory.name },
            ]}
          />

          <div className="mt-6">
            <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950">
              {activeCategory.name}
            </h1>
            <p className="mt-2 text-sm text-foreground-500 max-w-2xl">{activeCategory.description}</p>
          </div>

          <div className="mt-8 flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => handleCategorySwitch(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50 font-medium'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/60'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {categoryArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="group rounded-lg bg-background-100 border border-background-200/60 overflow-hidden hover:border-primary-200/50 transition-all cursor-pointer"
              >
                <div className="relative w-full h-44 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4">
                  <h3 className="text-base font-medium text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                    {article.title}
                  </h3>
                  <p className="mt-2 text-xs text-foreground-500 line-clamp-2 leading-relaxed">{article.excerpt}</p>
                  <div className="mt-3 flex items-center gap-3 text-xs text-foreground-400">
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      {article.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {article.readTime}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {categoryArticles.length === 0 && (
            <div className="mt-8 py-20 text-center">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-inbox-line text-2xl"></i>
              </div>
              <p className="mt-4 text-foreground-500 text-sm">该分类暂无文章</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}